import { GetCustomPageRequest } from '@app/_proto/Protos/custom_pages';
import { CustomPagesClient } from '@app/_proto/Protos/custom_pages.client';
import ContentComponent from '@app/components/content';
import Loading from '@app/components/loading';
import PageHeading from '@app/components/page.heading';
import PlaceholderComponent, { type PlaceholderProps } from '@app/components/placeholder';
import { StyledContentSection } from '@app/components/shared/text-pages.styles';
import { useGrpcApiWithQuery } from '@app/libs/api';
import { useGrpcRequest } from '@app/libs/grpc';
import { useHtmlToReact } from '@app/utils/html';
import { getHashCode } from '@app/utils/utils';
import { lazy, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { InView } from 'react-intersection-observer';
import { useNavigate, useParams } from 'react-router-dom';

//Load comments area only when user scrolls to that area, if ever.
const EntityCommentsArea = lazy(() => import('../../components/entity-comments/commentsarea'));

export default function CustomPage() {
    //#region : Get novel data

    //Get novel slug from query string
    const { slug1, slug2 } = useParams();

    const novelSlug = slug2 ? slug1 : undefined;
    const slug = slug2 ? slug2 : slug1;

    //If novel was not found, show 404 error
    const navigate = useNavigate();

    const novelRequest = useGrpcRequest(GetCustomPageRequest, {
        selector: {
            oneofKind: 'slug',
            slug: slug!,
        },
        novelSelector: novelSlug
            ? {
                  selector: {
                      oneofKind: 'novelSlug',
                      novelSlug: novelSlug!,
                  },
              }
            : undefined,
    });

    //Server request to fetch novel data
    const { data: pageData } = useGrpcApiWithQuery(
        CustomPagesClient,
        c => c.getCustomPage,
        novelRequest,
        ['page', novelSlug, slug],
        {
            staleTime: Number.POSITIVE_INFINITY, //Data never stales
            showModalOnError: true,
            suspense: true,
        }
    );

    const placeholderProps = useMemo<Omit<PlaceholderProps, 'item'>>(() => {
        const announcementHash = Math.abs(getHashCode(slug!));

        return {
            count: 8 - (announcementHash % 6),
            skeletonProps: idx => ({
                style: { marginBottom: (idx + 1) % 6 === 0 ? 24 : 8 },
                height: 14,
                width: announcementHash ? `${100 - ((announcementHash / (idx + 1)) % 80)}%` : undefined,
            }),
        };
    }, [slug]);

    const page = pageData?.item;

    useEffect(() => {
        if (pageData && !pageData.item) {
            navigate('/404', {
                replace: true,
            });
        }
    }, [pageData, navigate]);

    //#endregion : Get novel data

    const content = useHtmlToReact(page?.content || '');

    return (
        <div className="mb-[60px] px-[8px] sm:px-[6px] md:px-0">
            <Helmet defer={false}>
                <title>{page?.title}</title>
            </Helmet>
            <PageHeading className="mt-25" center>
                {page?.title}
            </PageHeading>
            <StyledContentSection centeredExtraMargin={true}>
                <div>
                    <PlaceholderComponent item={page} {...placeholderProps}>
                        <ContentComponent>{content}</ContentComponent>
                    </PlaceholderComponent>
                </div>
            </StyledContentSection>
            {!!page && page.allowComments && (
                <InView triggerOnce>
                    {({ inView, ref }) => (
                        <div ref={ref}>
                            {inView && !!page && (
                                <Loading>
                                    <EntityCommentsArea key={`comments_${slug}`} entityId={page.entityId} />
                                </Loading>
                            )}
                        </div>
                    )}
                </InView>
            )}
        </div>
    );
}
